import cn from 'classnames'
import { type ReactNode, useEffect, useMemo, useState } from 'react'

import { Translation } from 'cng-common/features/translations/components/translation'
import { Button } from 'core-components/library/button'
import { DropdownDynamic } from 'core-components/library/dropdown'
import { Item } from 'core-components/library/grid'
import { Input } from 'core-components/library/inputs'
import { IColumn } from 'core-components/library/list/types'
import { Loader } from 'core-components/library/loader'
import { ModalDialog } from 'core-components/library/modalDialog'
import { Tooltip } from 'core-components/library/tooltip'
import { Controller, ValidateInput, ValidationStatus } from 'core-components/library/validation'

import { getCleanEmail } from '../lib/getCleanEmail'
import { useUserCrud } from '../lib/useUserCrud'
import { checkFullEmail, checkSignerPost, checkString } from '../lib/validationHelpers'
import type {
  AcknowledgmentSignerOption,
  AcknowledgmentSignerPickerDialogProps,
  AcknowledgmentSignerPickerIntent,
  AcknowledgmentSignerPickerMode,
  AcknowledgmentSignerSelectionPayload,
  PbmHpContactRecord,
} from '../model/types'
import { useGlobalPartnerSignerOptions } from '../model/useGlobalPartnerSignerOptions'
import { usePartnerSignerPickerSource } from '../model/usePartnerSignerPickerSource'
import { usePbmSignerPickerSource } from '../model/usePbmSignerPickerSource'
import { SIGNER_PICKER_TRANSLATION_KEYS as K } from '../translations'

import { ParticipationField } from './participationField'
import { SignerAddButton } from './signerAddButton'
import { Table } from './Table'

import styles from './AcknowledgmentSignerPickerDialog.module.scss'

const FIELDS_TO_SEARCH_USER = ['UserAccountName']
const defaultManagerForm: AcknowledgmentSignerSelectionPayload = {
  email: '',
  firstName: '',
  lastName: '',
  role: '',
}

type PartnerContactRecord = {
  ContactID: string
  FirstName?: string
  LastName?: string
  Email?: string
  PR_HP_ROLE?: string
  PrimaryPartner?: boolean
}

type PickerContentProps = {
  mode: AcknowledgmentSignerPickerMode
  intent: AcknowledgmentSignerPickerIntent
  show: boolean
  partnerId: string
  onSelect: (payload: AcknowledgmentSignerSelectionPayload) => void
  initialSelection?: Partial<AcknowledgmentSignerSelectionPayload> | null
  excludedEmails?: string[]
  canAddOptional?: boolean
  isSubmitting?: boolean
  onClose: () => void
}

type DialogSectionHeaderProps = {
  title: ReactNode
  description?: ReactNode
  withInfoIcon?: boolean
}

const DialogSectionHeader = ({
  title,
  description,
  withInfoIcon = false,
}: DialogSectionHeaderProps) => (
  <div className={styles.sectionHeader}>
    <div className={styles.sectionTitle}>{title}</div>
    {description ? (
      <div
        className={cn(styles.sectionDescription, { [styles.sectionDescriptionInfo]: withInfoIcon })}
      >
        {withInfoIcon && <i className={cn('kbase kbase-info', styles.sectionDescriptionIcon)} />}
        <span className={styles.sectionDescriptionText}>{description}</span>
      </div>
    ) : null}
  </div>
)

const ManagerSignerPickerContent = ({
  show,
  onSelect,
  initialSelection,
  intent,
  onClose,
  isSubmitting,
  canAddOptional,
  excludedEmails,
}: Pick<
  PickerContentProps,
  | 'excludedEmails'
  | 'show'
  | 'onSelect'
  | 'initialSelection'
  | 'intent'
  | 'onClose'
  | 'isSubmitting'
  | 'canAddOptional'
>) => {
  const [form, setForm] = useState<AcknowledgmentSignerSelectionPayload>(defaultManagerForm)
  const [isChanged, setIsChanged] = useState(false)
  const [validationStatus, setValidationStatus] = useState(ValidationStatus.Invalid)
  const userCrud = useUserCrud(excludedEmails)
  const isOptional = Boolean(form.isOptional) && (intent !== 'add' || Boolean(canAddOptional))

  useEffect(() => {
    if (!show) {
      return
    }

    setForm({
      ...defaultManagerForm,
      email: initialSelection?.email ?? '',
      firstName: initialSelection?.firstName ?? '',
      lastName: initialSelection?.lastName ?? '',
      role: initialSelection?.role ?? '',
      isOptional: initialSelection?.isOptional ?? 0,
    })
    setIsChanged(false)
    setValidationStatus(ValidationStatus.Invalid)
  }, [initialSelection, show])

  const onChange = (value: string, field: keyof AcknowledgmentSignerSelectionPayload) => {
    setForm((prev) => ({
      ...prev,
      [field]: field === 'email' ? getCleanEmail(value) : value,
    }))
    setIsChanged(true)
  }

  const onEmailChange = (value: string, item?: IObjectAny) => {
    if (item?.Id) {
      setForm({
        ...form,
        email: item.Id as string,
        firstName: item.FirstName as string,
        lastName: item.LastName as string,
        role: (item.UserPost as string | null) ?? '',
      })
    } else {
      setForm((prev) => ({ ...prev, email: getCleanEmail(value) }))
    }

    setIsChanged(true)
  }

  return (
    <div className={styles.formRoot}>
      <div className={cn(styles.sectionDescription, styles.sectionDescriptionInfo)}>
        <i className={cn('kbase kbase-info', styles.sectionDescriptionIcon)} />
        <span className={styles.sectionDescriptionText}>
          <Translation text="jbp-commitment-manager-dialog-info-icon-content" />
        </span>
      </div>

      <div className={styles.fields}>
        <Controller
          onValidationStatusChange={({ status }) => {
            if (status !== 'WIP') {
              setValidationStatus(status)
            }
          }}
        >
          <Item xs={12}>
            <DropdownDynamic
              classes={{
                controlClasses: {
                  root: styles.input,
                  popPaper: styles.rolePaper,
                },
              }}
              label="Email *"
              fieldsToSearch={FIELDS_TO_SEARCH_USER}
              calculateCaption={(item: IObjectAny) =>
                `${item.FirstName} ${item.LastName} <${item.Id}>`
              }
              value={form.email}
              source={userCrud}
              checkFn={checkFullEmail}
              onChange={onEmailChange}
            />
          </Item>
          <Item xs={12} className={styles.nameField}>
            <ValidateInput
              classes={{
                validatorClasses: {
                  root: styles.input,
                },
              }}
              type="text"
              label="First Name *"
              value={form.firstName}
              onChange={(value: string) => onChange(value, 'firstName')}
              checkFn={(value) => checkString(value, 'Please, enter first name')}
              field="firstName"
            />
          </Item>
          <Item xs={12} className={styles.nameField}>
            <ValidateInput
              classes={{
                validatorClasses: {
                  root: styles.input,
                },
              }}
              type="text"
              label="Last Name *"
              value={form.lastName}
              onChange={(value: string) => onChange(value, 'lastName')}
              checkFn={(value) => checkString(value, 'Please, enter last name')}
              field="lastName"
            />
          </Item>
          <Item xs={12}>
            <ValidateInput
              classes={{
                validatorClasses: {
                  root: styles.input,
                },
              }}
              type="text"
              label="Post"
              value={form.role ?? ''}
              onChange={(value: string) => onChange(value, 'role')}
              checkFn={checkSignerPost}
              field="role"
            />
          </Item>
        </Controller>
      </div>

      {intent === 'add' && (
        <div className={styles.managerParticipation}>
          <ParticipationField
            value={isOptional ? 1 : 0}
            onChange={(isOptional) => setForm((prev) => ({ ...prev, isOptional }))}
            disabled={isSubmitting}
            optionalDisabled={!canAddOptional}
          />
        </div>
      )}
      <div className={styles.buttonLine}>
        <Button variant="outlined" onClick={onClose} disabled={isSubmitting}>
          <Translation text={K.cancel} />
        </Button>
        <Button
          className={styles.button}
          loading={isSubmitting}
          disabled={!isChanged || validationStatus !== ValidationStatus.Valid}
          onClick={() => {
            onSelect({
              ...form,
              isOptional: isOptional ? 1 : 0,
              role: form.role?.trim() || undefined,
            })
          }}
        >
          {intent === 'add' ? (
            <Translation text={isOptional ? K.addOptional : K.addMandatory} />
          ) : (
            <Translation text="jbp-apply" span />
          )}
        </Button>
      </div>
    </div>
  )
}

const PbmSignerPickerContent = ({
  partnerId,
  onSelect,
  intent,
  canAddOptional,
  excludedEmails,
}: Pick<
  PickerContentProps,
  'partnerId' | 'onSelect' | 'intent' | 'canAddOptional' | 'excludedEmails'
>) => {
  const source = usePbmSignerPickerSource(partnerId, excludedEmails)

  const columns = useMemo<IColumn[]>(
    () => [
      {
        field: 'TicketId',
        title: 'partnerContacts-contact',
        priority: 1,
        renderFunction: (item: PbmHpContactRecord) => {
          const fullName = [item.FirstName, item.LastName].filter(Boolean).join(' ').trim() || '—'

          return (
            <Tooltip
              inline
              tipContent={() => (
                <>
                  {fullName}
                  {item.Email ? (
                    <>
                      <br />
                      {item.Email}
                    </>
                  ) : null}
                </>
              )}
            >
              <div className={styles.contactCell}>
                <span className={styles.contactName}>{fullName}</span>
                {item.Email ? <span className={styles.contactEmail}>{item.Email}</span> : null}
              </div>
            </Tooltip>
          )
        },
      },
      {
        field: 'BusinessUnit',
        title: 'partnerContacts-businessunit',
        priority: 2,
      },
      {
        field: 'Roles',
        title: 'partnerContacts-roles',
        priority: 3,
      },
      {
        field: 'LeadPBM',
        title: 'jbp-partner-contacts-leadpbm',
        priority: 4,
        renderFunction: (item: PbmHpContactRecord) => (item.LeadPBM ? 'Yes' : 'No'),
      },
      {
        field: 'AssignmentPeriod',
        title: 'jbp-partner-contacts-assignmentperiod',
        priority: 5,
      },
      {
        field: 'acknowledgmentAdd',
        title: '',
        disableSort: true,
        hideSettings: true,
        hideSortIcon: true,
        hideSortIndex: true,
        hideFilterIcon: true,
        tooltipVisible: false,
        priority: 6,
        renderFunction: (item: PbmHpContactRecord) => (
          <SignerAddButton
            canAddOptional={canAddOptional}
            intent={intent}
            onSelect={onSelect}
            signer={{
              email: item.Email || '',
              firstName: item.FirstName || '',
              lastName: item.LastName || '',
              role: item.Roles || undefined,
            }}
          />
        ),
      },
    ],
    [intent, onSelect, canAddOptional]
  )

  return (
    <div className={styles.content}>
      <div className={styles.infoContainer}>
        <i className={`kbase kbase-info ${styles.infoIcon}`} aria-hidden="true" />
        <Translation text="jbp-hpcontacts-info" span actionsInside />
      </div>

      <Table<PbmHpContactRecord> header pagination hasTranslate source={source} columns={columns} />
    </div>
  )
}

const RegularPartnerSignerPickerContent = ({
  partnerId,
  excludedEmails,
  onSelect,
  intent,
  canAddOptional,
}: Pick<
  PickerContentProps,
  'partnerId' | 'excludedEmails' | 'onSelect' | 'intent' | 'canAddOptional'
>) => {
  const { source, isLoading } = usePartnerSignerPickerSource(partnerId, excludedEmails)

  const columns = useMemo<IColumn[]>(
    () => [
      {
        field: 'FirstName',
        title: 'First Name',
        priority: 1,
      },
      {
        field: 'LastName',
        title: 'Last Name',
        priority: 2,
      },
      {
        field: 'Email',
        title: 'Email',
        priority: 3,
      },
      {
        field: 'PR_HP_ROLE',
        title: 'Hp Roles',
        priority: 4,
      },
      {
        field: 'PrimaryPartner',
        title: 'Primary',
        priority: 5,
        renderFunction: (item: PartnerContactRecord) => (item.PrimaryPartner ? 'Yes' : 'No'),
      },
      {
        field: 'acknowledgmentAdd',
        title: '',
        disableSort: true,
        hideSettings: true,
        hideSortIcon: true,
        hideSortIndex: true,
        hideFilterIcon: true,
        tooltipVisible: false,
        priority: 6,
        renderFunction: (item: PartnerContactRecord) => (
          <SignerAddButton
            canAddOptional={canAddOptional}
            intent={intent}
            onSelect={onSelect}
            signer={{
              email: item.Email || '',
              firstName: item.FirstName || '',
              lastName: item.LastName || '',
              contactId: item.ContactID,
            }}
          />
        ),
      },
    ],
    [intent, onSelect, canAddOptional]
  )

  if (isLoading || !source) {
    return (
      <div className={`${styles.content} ${styles.loader}`}>
        <Loader />
      </div>
    )
  }

  return (
    <div className={styles.content}>
      <Table<PartnerContactRecord> header pagination source={source} columns={columns} />
    </div>
  )
}

const getGlobalPartnerDescription = (intent: AcknowledgmentSignerPickerIntent) => {
  if (intent === 'change') {
    return 'Select a global partner contact to replace the current Partner acknowledgment signer.'
  }

  if (intent === 'select') {
    return 'Select a global partner contact for the Partner acknowledgment entry.'
  }

  return 'Select a global partner contact to add to the Partner acknowledgment list.'
}

const GlobalPartnerSignerPickerContent = ({
  show,
  partnerId,
  intent,
  excludedEmails,
  onSelect,
  canAddOptional,
}: Pick<
  PickerContentProps,
  'show' | 'partnerId' | 'intent' | 'excludedEmails' | 'onSelect' | 'canAddOptional'
>) => {
  const [searchValue, setSearchValue] = useState('')
  const { options, isLoading, error } = useGlobalPartnerSignerOptions(partnerId, show)

  useEffect(() => {
    if (show) {
      setSearchValue('')
    }
  }, [show])

  const filteredOptions = useMemo(() => {
    const normalizedValue = searchValue.trim().toLowerCase()
    const excluded = new Set(excludedEmails?.map((email) => email.trim().toLowerCase()))
    const availableOptions = options.filter(
      (option) => !excluded.has(option.email.trim().toLowerCase())
    )

    if (!normalizedValue) {
      return availableOptions
    }

    return availableOptions.filter((option) =>
      [option.fullName, option.email, option.secondaryLabel, option.tertiaryLabel]
        .filter(Boolean)
        .some((value) => value?.toLowerCase().includes(normalizedValue))
    )
  }, [excludedEmails, options, searchValue])

  return (
    <div className={cn(styles.content, styles.selectionRoot)}>
      <DialogSectionHeader
        title="Partner acknowledgment"
        description={getGlobalPartnerDescription(intent)}
      />

      <Input
        classes={{
          root: styles.searchInput,
        }}
        value={searchValue}
        onChange={setSearchValue}
        startContent={<i className="kbase kbase-search" />}
        placeholder="Search global partner contacts"
      />

      <div className={styles.selectionList}>
        {isLoading ? (
          <div className={cn(styles.emptyState, styles.loaderState)}>
            <Loader />
          </div>
        ) : error ? (
          <div className={styles.emptyState}>Failed to load contacts.</div>
        ) : filteredOptions.length === 0 ? (
          <div className={styles.emptyState}>
            {searchValue ? 'No matching contacts found.' : 'No contacts available.'}
          </div>
        ) : (
          filteredOptions.map((option: AcknowledgmentSignerOption) => (
            <div key={option.id} className={styles.optionCard}>
              <div className={styles.optionInfo}>
                <div className={styles.optionName}>{option.fullName}</div>
                <div className={styles.optionEmail}>{option.email}</div>
                {option.secondaryLabel ? (
                  <div className={styles.optionMeta}>{option.secondaryLabel}</div>
                ) : null}
                {option.tertiaryLabel ? (
                  <div className={styles.optionMeta}>{option.tertiaryLabel}</div>
                ) : null}
              </div>

              <SignerAddButton
                canAddOptional={canAddOptional}
                intent={intent}
                onSelect={onSelect}
                signer={{
                  email: option.email,
                  firstName: option.firstName,
                  lastName: option.lastName,
                  contactId: option.contactId,
                }}
              />
            </div>
          ))
        )}
      </div>
    </div>
  )
}

const AcknowledgmentSignerPickerContent = ({
  mode,
  intent,
  show,
  partnerId,
  onSelect,
  initialSelection,
  excludedEmails,
  isSubmitting,
  canAddOptional,
  onClose,
}: PickerContentProps) => {
  if (mode === 'manager') {
    return (
      <ManagerSignerPickerContent
        excludedEmails={excludedEmails}
        show={show}
        initialSelection={initialSelection}
        intent={intent}
        onClose={onClose}
        canAddOptional={canAddOptional}
        isSubmitting={isSubmitting}
        onSelect={onSelect}
      />
    )
  }

  if (isSubmitting) {
    return (
      <div className={cn(styles.content, styles.loader)}>
        <Loader />
      </div>
    )
  }

  if (mode === 'pbm') {
    return (
      <PbmSignerPickerContent
        excludedEmails={excludedEmails}
        canAddOptional={canAddOptional}
        intent={intent}
        partnerId={partnerId}
        onSelect={onSelect}
      />
    )
  }

  if (partnerId.startsWith('GP')) {
    return (
      <GlobalPartnerSignerPickerContent
        canAddOptional={canAddOptional}
        show={show}
        partnerId={partnerId}
        intent={intent}
        excludedEmails={excludedEmails}
        onSelect={onSelect}
      />
    )
  }

  return (
    <RegularPartnerSignerPickerContent
      canAddOptional={canAddOptional}
      intent={intent}
      partnerId={partnerId}
      excludedEmails={excludedEmails}
      onSelect={onSelect}
    />
  )
}

const getDialogTitle = ({
  mode,
  intent,
}: Pick<AcknowledgmentSignerPickerDialogProps, 'mode' | 'intent'>) => {
  if (mode === 'manager') {
    if (intent === 'change') {
      return <Translation text="jbp-commitment-manager-change" />
    }

    if (intent === 'select') {
      return <Translation text="jbp-commitment-manager-select" />
    }

    return <Translation text="jbp-manager-dialog-header" />
  }

  if (mode === 'pbm') {
    if (intent === 'add') {
      return <Translation text="jbp-hpcontacttable-title" />
    }

    return <Translation text="jbp-commitment-pbm-change" />
  }

  if (intent === 'change') {
    return <Translation text="partnercommitment-change-contact" />
  }

  if (intent === 'select') {
    return <Translation text="partnercommitment-select-contact" />
  }

  return <Translation text="jbp-contacttable-title" />
}

export const AcknowledgmentSignerPickerDialog = ({
  mode,
  intent,
  show,
  partnerId,
  onClose,
  onSelect,
  initialSelection,
  excludedEmails,
  isSubmitting,
  canAddOptional,
  title,
}: AcknowledgmentSignerPickerDialogProps) => (
  <ModalDialog
    closeOnOutsideClick={false}
    show={show}
    onClose={onClose}
    size={mode === 'manager' ? 'sm' : 'lg'}
    classes={{
      window: cn(styles.window, mode !== 'manager' && styles.windowWide),
      body: styles.body,
    }}
    header={() => (
      <div className={styles.dialogHeader}>
        <div className={styles.title}>{title ?? getDialogTitle({ mode, intent })}</div>
        {intent === 'add' && mode !== 'manager' && (
          <div className={styles.sectionDescription}>
            <Translation text={mode === 'pbm' ? K.addingPbm : K.addingPartner} />
          </div>
        )}
      </div>
    )}
    content={AcknowledgmentSignerPickerContent}
    contentProps={{
      mode,
      intent,
      show,
      partnerId,
      onSelect,
      initialSelection,
      excludedEmails,
      isSubmitting,
      canAddOptional,
      onClose,
    }}
  />
)
